import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@moe/oss/ui/card";
import { Input } from "@moe/oss/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@moe/oss/ui/select";
import { Slider } from "@moe/oss/ui/slider";
import { StatusButton } from "@moe/oss/ui/status-button";
import { Textarea } from "@moe/oss/ui/textarea";
import { TipBubble } from "@moe/priv/components/tip-bubble";
import { AGFModel } from "@moe/priv/types/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APISettings, apiSettingsQK, useAPISettings } from "@web/hooks/use-api-settings";
import { client } from "@web/lib/trpc";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useState } from "react";
import { toast } from "sonner";

interface Props {
  chatID: number;
}

const models: Record<AGFModel | "dynamic", string> = {
  dynamic:
    "(Recommended) Randomly uses a different model for each message / regeneration. This helps with diversity, and is results in faster responses.",
  magmell: "inflatebot/MN-12B-Mag-Mell-R1. A merge of multiple other models.",
  stheno: "Sao10K/L3-8B-Stheno-v3.2. A finetune of Llama 3 8b by Sao10K!",
  exp0000: "🧪 Beta - Experimental model. May be unstable. Let me know your thoughts!"
};

/**
 * Renders the API Settings Modal component.
 * Allows the user to configure the API provider settings for the application.
 * The API settings are stored in the user's local storage and used in the chat service to generate responses.
 */
export function APISettingsModal({ chatID }: Props) {
  const initialSettings = useAPISettings();
  const [settings, setSettings] = useState<APISettings>(initialSettings);
  const queryClient = useQueryClient();
  const { closeModal } = useAppContext();

  /**
   * Performs a mutation to test the configured API provider settings.
   * This mutation sends a test request to the configured API provider and throws an error if the test fails.
   */
  const testAPIMutation = useMutation({
    mutationFn: async () => {
      const res = await client.chat.testProxy.query({
        kind: settings.provider as any,
        apiKey: settings.apiKey,
        baseURL: settings.baseURL,
        model: settings.model,
        maxContextTokens: settings.maxContextTokens
      });

      if (!res) throw new Error("API provider test failed :<");
    }
  });

  /**
   * Save the current API settings to the user's local storage.
   * Then invalidates the API settings query cache to ensure the updated settings are used.
   */
  const saveMutation = useMutation({
    mutationFn: async () => {
      localStorage.setItem(apiSettingsQK, JSON.stringify(settings));
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", chatID] });
      queryClient.invalidateQueries({ queryKey: [apiSettingsQK] });
      toast.success("Saved!");
      closeModal();
    },
    onError: () => {
      toast.error("Failed to save api settings :<");
    }
  });

  const updateSettings = (field: keyof APISettings, value: string | number) => {
    setSettings((prev) => ({
      ...prev,
      [field]: field === "maxContextTokens" ? Number(value) : value
    }));
  };

  return (
    <Card className="text-tx-primary bg-float scroll-primary max-h-[75vh] w-full overflow-y-auto border-none md:w-[32rem] md:border">
      <CardHeader>
        <CardTitle>API Settings</CardTitle>
        <CardDescription>Configure your API provider settings</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label htmlFor="provider" className="font-medium">
            API Provider
          </label>
          <Select value={settings.provider} onValueChange={(v: "anime.gf" | "proxy") => updateSettings("provider", v)}>
            <SelectTrigger className="w-28 md:w-32">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent className="" portal={false}>
              <SelectItem value="anime.gf">
                <p>anime.gf</p>
              </SelectItem>
              <SelectItem value="proxy">
                <p>Proxy</p>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {settings.provider === "anime.gf" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="model" className="font-medium">
                Model
              </label>
              <TipBubble
                text={
                  "Choose from four models: Dynamic mode (recommended) picks a random model each time for faster, diverse responses."
                }
              />
            </div>

            <Select value={settings.model} onValueChange={(v) => updateSettings("model", v)} defaultValue="dynamic">
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent portal={false}>
                {Object.entries(models).map(([key, _description]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex w-full flex-col [overflow-wrap:anywhere]">
                      <span className="font-medium">{key}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {settings.provider === "proxy" && (
          <>
            <div className="text-tx-tertiary text-sm font-medium">
              anime.gf is not affiliated with any external API. <br />
              Make sure you trust your API provider. <br />
              They have the power to log your chats!!
            </div>

            <div className="space-y-2">
              <label htmlFor="apiKey" className="text-sm font-medium">
                API Key
              </label>
              <Input
                id="apiKey"
                value={settings.apiKey}
                onChange={(e) => updateSettings("apiKey", e.target.value)}
                placeholder="Enter the API key of your proxy if required."
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="baseURL" className="text-sm font-medium">
                Base URL
              </label>
              <Input
                id="baseURL"
                value={settings.baseURL}
                onChange={(e) => updateSettings("baseURL", e.target.value)}
                placeholder="Enter URL of your proxy: (e.g., https://api.example.com/v1)"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="model" className="text-sm font-medium">
                Model
              </label>
              <Input
                id="model"
                value={settings.model}
                onChange={(e) => updateSettings("model", e.target.value)}
                placeholder="Enter model name if required by your proxy."
              />
            </div>

            <div className="space-y-4">
              <label htmlFor="maxContextTokens" className="text-sm font-medium">
                Max Context Tokens
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Slider
                    value={[settings.maxContextTokens]}
                    onValueChange={(value) => updateSettings("maxContextTokens", value[0])}
                    max={128000}
                    min={1000}
                    step={1000}
                  />
                </div>
                <Input
                  type="number"
                  value={settings.maxContextTokens}
                  onChange={(e) => updateSettings("maxContextTokens", Number(e.target.value))}
                  className="w-24"
                  min={1000}
                  max={128000}
                  step={1000}
                />
              </div>
              <p className="text-tx-tertiary text-sm">
                Controls how much conversation history is sent to the AI. Higher values allow for longer memory but some
                proxy provider might not allow this. Recommended range: 6000-16000 tokens depending on your needs and
                model limits.
              </p>
            </div>
          </>
        )}

        {settings.provider === "proxy" && (
          <div className="space-y-2">
            <label htmlFor="customInstructions" className="font-medium">
              Jailbreak
            </label>
            <Textarea
              id="customInstructions"
              value={settings.customInstructions}
              onChange={(e) => updateSettings("customInstructions", e.target.value)}
              placeholder="You could enter a jailbreak prompt if needed here (optional)."
            />
          </div>
        )}
      </CardContent>

      <CardFooter className="flex items-center justify-end gap-6 pt-6">
        {settings.provider === "proxy" && (
          <StatusButton
            statusConfig={{
              idle: { text: "Test API" },
              pending: { text: "This may take up to 30 seconds..." },
              success: { text: "All Good!", tooltip: "Connection successful!" },
              error: { text: "API Error", tooltip: "Failed to connect. Check your settings." }
            }}
            status={testAPIMutation.status}
            onClick={testAPIMutation.mutate}
          />
        )}
        <Button onClick={saveMutation.mutate} disabled={saveMutation.isPending}>
          <PencilSquareIcon />
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}
